import { useState } from 'react';
import { Pencil, X, Check } from 'lucide-react';

const inputClass = 'w-full px-3.5 py-2.5 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-shadow';

function Row({ label, children }) {
  return (
    <div className="flex gap-3">
      <span className="text-muted-foreground w-32 shrink-0 font-medium text-sm">{label}</span>
      <span className="text-sm text-foreground">{children}</span>
    </div>
  );
}

export function PersonalInfoSection({ user, personalForm, setPersonalForm, saving, onSave }) {
  const [editing, setEditing] = useState(false);

  const handleSave = async () => {
    const ok = await onSave();
    if (ok) setEditing(false);
  };

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-2xl font-light text-foreground">Mijn gegevens</h2>
        {!editing && (
          <button data-testid="edit-personal-btn" onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 text-sm text-primary font-medium hover:underline">
            <Pencil className="w-3.5 h-3.5" /> Bewerken
          </button>
        )}
      </div>
      {editing ? (
        <div className="bg-card border border-border rounded-2xl p-6 space-y-4 shadow-sm" data-testid="personal-edit-form">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1.5">Voornaam</label>
              <input data-testid="edit-first-name" value={personalForm.firstName} onChange={e => setPersonalForm(f => ({ ...f, firstName: e.target.value }))} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1.5">Achternaam</label>
              <input data-testid="edit-last-name" value={personalForm.lastName} onChange={e => setPersonalForm(f => ({ ...f, lastName: e.target.value }))} className={inputClass} /></div>
          </div>
          <div><label className="block text-sm font-medium mb-1.5">Telefoon</label>
            <input data-testid="edit-phone" type="tel" value={personalForm.phone} onChange={e => setPersonalForm(f => ({ ...f, phone: e.target.value }))} className={inputClass} /></div>
          <div className="flex gap-3">
            <button data-testid="save-personal-btn" onClick={handleSave} disabled={saving}
              className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-full px-6 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 transition-all active:scale-95">
              <Check className="w-4 h-4" /> {saving ? 'Opslaan...' : 'Opslaan'}
            </button>
            <button onClick={() => setEditing(false)}
              className="inline-flex items-center gap-1.5 px-6 py-2.5 border border-border rounded-full text-sm font-medium hover:bg-muted transition-colors">
              <X className="w-4 h-4" /> Annuleren
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-2xl p-6 space-y-3 shadow-sm">
          <Row label="Naam">{[personalForm.firstName, personalForm.lastName].filter(Boolean).join(' ') || '--'}</Row>
          <Row label="Telefoon">{personalForm.phone || '--'}</Row>
          <Row label="E-mail">{user.email}</Row>
        </div>
      )}
    </section>
  );
}

export function AddressSection({ profile, addressForm, setAddressForm, saving, onSave }) {
  const [editing, setEditing] = useState(false);

  const handleSave = async () => {
    const ok = await onSave();
    if (ok) setEditing(false);
  };

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-2xl font-light text-foreground">Mijn adres</h2>
        {!editing && (
          <button data-testid="edit-address-btn" onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 text-sm text-primary font-medium hover:underline">
            <Pencil className="w-3.5 h-3.5" /> Bewerken
          </button>
        )}
      </div>
      {editing ? (
        <div className="bg-card border border-border rounded-2xl p-6 space-y-4 shadow-sm" data-testid="address-edit-form">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><label className="block text-sm font-medium mb-1.5">Straat</label>
              <input value={addressForm.street} onChange={e => setAddressForm(f => ({ ...f, street: e.target.value }))} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1.5">Nr.</label>
              <input value={addressForm.number} onChange={e => setAddressForm(f => ({ ...f, number: e.target.value }))} className={inputClass} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-sm font-medium mb-1.5">Bus</label>
              <input value={addressForm.bus} onChange={e => setAddressForm(f => ({ ...f, bus: e.target.value }))} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1.5">Postcode</label>
              <input value={addressForm.postalCode} onChange={e => setAddressForm(f => ({ ...f, postalCode: e.target.value }))} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1.5">Stad</label>
              <input value={addressForm.city} onChange={e => setAddressForm(f => ({ ...f, city: e.target.value }))} className={inputClass} /></div>
          </div>
          <div className="flex gap-3">
            <button data-testid="save-address-btn" onClick={handleSave} disabled={saving}
              className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-full px-6 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 transition-all active:scale-95">
              <Check className="w-4 h-4" /> {saving ? 'Opslaan...' : 'Opslaan'}
            </button>
            <button onClick={() => setEditing(false)}
              className="inline-flex items-center gap-1.5 px-6 py-2.5 border border-border rounded-full text-sm font-medium hover:bg-muted transition-colors">
              <X className="w-4 h-4" /> Annuleren
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
          {profile.street ? (
            <div className="space-y-1 text-sm">
              <p className="text-foreground">{[profile.street, profile.number, profile.bus].filter(Boolean).join(' ')}</p>
              <p className="text-foreground">{profile.postalCode} {profile.city}</p>
              <p className="text-foreground">{profile.country}</p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Nog geen adres opgeslagen.</p>
          )}
        </div>
      )}
    </section>
  );
}

export function B2BOrgSection({ b2bPerson, b2bOrg }) {
  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-2xl font-light text-foreground">Organisatie</h2>
        <a href="/b2b" className="text-sm text-primary font-medium hover:underline">B2B portaal</a>
      </div>
      <div className="bg-card border border-border rounded-2xl p-6 space-y-3 shadow-sm">
        <Row label="Bedrijf">{b2bOrg?.companyName || '--'}</Row>
        <Row label="BTW-nummer">{b2bOrg?.vatNumber || '--'}</Row>
        {b2bOrg?.addressStreet && (
          <Row label="Adres">
            {[b2bOrg.addressStreet, b2bOrg.addressNumber, b2bOrg.addressBus].filter(Boolean).join(' ')}
            {b2bOrg.addressCity ? `, ${b2bOrg.addressPostalCode} ${b2bOrg.addressCity}` : ''}
          </Row>
        )}
        <Row label="Status">
          <span className={b2bPerson?.isApproved ? 'text-green-600 font-medium' : 'text-amber-600 font-medium'}>
            {b2bPerson?.isApproved ? 'Goedgekeurd' : 'In afwachting van goedkeuring'}
          </span>
        </Row>
      </div>
    </section>
  );
}
